import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Users, Plus, Trash2, Power, Loader2, Briefcase, Mail, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';

const MODULO_LABELS = {
  resumen: 'Resumen (dashboard)',
  empleados: 'Ver empleados',
  empleados_editar: 'Editar empleados',
  centros: 'Centros',
  turnos: 'Turnos',
  fichajes: 'Ver fichajes',
  fichajes_editar: 'Editar fichajes',
  calendario: 'Calendario',
  comunicados: 'Comunicados',
  informes: 'Informes',
  formacion: 'Formación',
  ausencias: 'Ausencias',
  facturacion: 'Facturación',
  ajustes: 'Ajustes',
  colaboradores: 'Colaboradores',
  auditoria: 'Auditoría',
};

const ROLES = [
  { value: 'colaborador', label: 'Colaborador', hint: 'Persona interna con acceso al panel' },
  { value: 'gestoria', label: 'Gestoría', hint: 'Asesoría externa (contabilidad, nóminas)' },
  { value: 'auditor', label: 'Auditor', hint: 'Auditor externo o inspección' },
];
const ROLE_COLOR = {
  colaborador: 'bg-blue-100 text-blue-800',
  gestoria: 'bg-purple-100 text-purple-800',
  auditor: 'bg-amber-100 text-amber-800',
};

const empty = { username: '', password: '', full_name: '', email: '', telefono: '', role: 'colaborador' };

export default function ColaboradoresCard() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [creando, setCreando] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    try { setItems((await api.get('/admin/colaboradores')).data); }
    catch { /* silencio */ }
    finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, []);

  const crear = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) return toast.error('El DNI/NIE es obligatorio');
    if (form.password.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres');
    if (!form.full_name.trim()) return toast.error('El nombre completo es obligatorio');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error('Email no válido');
    setCreando(true);
    try {
      await api.post('/admin/colaboradores', form);
      toast.success(`${ROLES.find((r) => r.value === form.role)?.label} creado`);
      setForm(empty);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally { setCreando(false); }
  };

  const toggleActivo = async (c) => {
    try {
      await api.put(`/admin/colaboradores/${c.id}`, { activo: !c.activo });
      toast.success(c.activo ? 'Desactivado' : 'Activado');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (c) => {
    try {
      await api.delete(`/admin/colaboradores/${c.id}`);
      toast.success('Usuario eliminado');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const [invitandoId, setInvitandoId] = useState(null);
  const [permOpen, setPermOpen] = useState(false);
  const [permEditing, setPermEditing] = useState(null); // {id, full_name, permisos (efectivos), permisos_override}
  const [permForm, setPermForm] = useState({});
  const enviarInvitacion = async (c) => {
    setInvitandoId(c.id);
    try {
      const panel_url = `${window.location.origin}/admin/login`;
      const { data } = await api.post(`/admin/colaboradores/${c.id}/invitacion`, { panel_url });
      toast.success(`Invitación enviada a ${data.email}. Se ha generado una contraseña temporal.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la invitación');
    } finally { setInvitandoId(null); }
  };

  const abrirPermisos = (c) => {
    setPermEditing(c);
    setPermForm({ ...(c.permisos || {}) });
    setPermOpen(true);
  };
  const guardarPermisos = async () => {
    if (!permEditing) return;
    try {
      await api.put(`/admin/colaboradores/${permEditing.id}/permisos`, { permisos: permForm });
      toast.success('Permisos actualizados');
      setPermOpen(false); cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <Card className="border-zinc-200 lg:col-span-2" data-testid="colaboradores-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" /> Usuarios de la empresa
          <Badge variant="secondary" className="ml-1 font-normal">{items.length}</Badge>
        </CardTitle>
        <CardDescription>
          Añade personas de tu equipo o de gestorías/auditorías externas. Cada rol tiene una matriz de permisos
          por defecto que puedes ajustar por usuario con el botón &quot;Permisos&quot;.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Formulario ------------------------------------------------ */}
        <form onSubmit={crear} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60">
          <div className="md:col-span-2 flex items-center gap-2 text-sm font-semibold text-zinc-700">
            <Plus className="h-4 w-4" /> Añadir usuario
          </div>
          <div className="space-y-1.5">
            <Label>DNI / NIE (será su usuario) *</Label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toUpperCase() })}
              placeholder="Ej: 12345678Z" maxLength={10} required data-testid="colab-username" />
            <p className="text-xs text-zinc-500">Mismo formato que empleados. Con este DNI accederá al panel.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Contraseña *</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Mínimo 8 caracteres" minLength={8} required data-testid="colab-password" />
          </div>
          <div className="space-y-1.5">
            <Label>Nombre completo *</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required data-testid="colab-fullname" />
          </div>
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              required data-testid="colab-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Teléfono</Label>
            <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              data-testid="colab-telefono" />
          </div>
          <div className="space-y-1.5">
            <Label>Rol *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="colab-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    <div className="flex flex-col">
                      <span>{r.label}</span>
                      <span className="text-xs text-zinc-500">{r.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={creando} className="bg-zinc-900 hover:bg-zinc-800" data-testid="colab-crear-btn">
              {creando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
              {creando ? 'Creando...' : 'Añadir usuario'}
            </Button>
          </div>
        </form>

        {/* Listado --------------------------------------------------- */}
        <div className="space-y-2">
          {cargando && <p className="text-sm text-zinc-400 text-center py-4">Cargando...</p>}
          {!cargando && items.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-6">
              Aún no has añadido ningún usuario. Rellena el formulario para el primero.
            </p>
          )}
          {items.map((c) => (
            <div key={c.id} className={`flex flex-wrap items-center gap-3 border rounded-lg px-3 py-2.5 ${c.activo ? 'border-zinc-200 bg-white' : 'border-zinc-200 bg-zinc-50/70 opacity-70'}`}
              data-testid={`colab-item-${c.username}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-800 truncate">{c.full_name}
                  <span className="text-xs font-normal text-zinc-500 ml-2">@{c.username}</span>
                </p>
                <p className="text-xs text-zinc-500 truncate">
                  {c.email}{c.telefono ? ` · ${c.telefono}` : ''}
                </p>
              </div>
              <Badge className={`${ROLE_COLOR[c.role] || 'bg-zinc-100 text-zinc-700'}`}><Briefcase className="h-3 w-3 mr-1" />{c.role_label}</Badge>
              {!c.activo && <Badge variant="outline" className="text-zinc-500">Desactivado</Badge>}
              <div className="flex items-center gap-1">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-600 hover:text-zinc-900"
                      disabled={!c.activo || invitandoId === c.id}
                      title={c.activo ? 'Enviar invitación por email (genera nueva contraseña)' : 'Actívalo primero para poder invitarle'}
                      data-testid={`colab-invitar-${c.username}`}>
                      {invitandoId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Enviar invitación a {c.full_name}</AlertDialogTitle>
                      <AlertDialogDescription>
                        Se generará una <strong>nueva contraseña temporal</strong> y se enviará a{' '}
                        <strong>{c.email}</strong> junto con el enlace de acceso al panel.
                        La contraseña anterior dejará de funcionar en cuanto envíes la invitación.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction className="bg-zinc-900 hover:bg-zinc-800"
                        onClick={() => enviarInvitacion(c)}
                        data-testid={`colab-invitar-confirm-${c.username}`}>
                        Enviar invitación
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => abrirPermisos(c)}
                  title="Editar permisos por módulo" data-testid={`colab-permisos-${c.username}`}>
                  <Shield className="h-4 w-4 text-indigo-600" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleActivo(c)}
                  title={c.activo ? 'Desactivar' : 'Activar'} data-testid={`colab-toggle-${c.username}`}>
                  <Power className={`h-4 w-4 ${c.activo ? 'text-emerald-600' : 'text-zinc-400'}`} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`colab-delete-${c.username}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar a {c.full_name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Perderá el acceso al panel de forma permanente. Esta acción no se puede deshacer.
                        Si sólo quieres retirarle el acceso temporalmente, mejor usa el botón de desactivar.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(c)}>Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent data-testid="permisos-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Permisos de {permEditing?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
            <p className="text-xs text-zinc-500 mb-3">
              Activa o desactiva el acceso a cada módulo. Se aplica la próxima vez que el usuario entre al panel.
            </p>
            {Object.entries(MODULO_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center justify-between gap-3 py-1.5 border-b border-zinc-100" data-testid={`perm-row-${k}`}>
                <span className="text-sm text-zinc-700">{label}</span>
                <Switch checked={!!permForm[k]}
                  onCheckedChange={(v) => setPermForm((f) => ({ ...f, [k]: v }))}
                  data-testid={`perm-switch-${k}`} />
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermOpen(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardarPermisos} data-testid="perm-guardar-btn">
              Guardar permisos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
